<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();

const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const [showActionsDropdown, toggleDropdown] = useToggle();

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.ALL;
});

const currentSortBy = computed(() => {
  return (
    chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const chatStatusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open_and_pending.TEXT'),
    value: 'open_and_pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

const chatSortOptions = computed(() => [
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_asc.TEXT'),
    value: 'last_activity_at_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_desc.TEXT'),
    value: 'last_activity_at_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_desc.TEXT'),
    value: 'created_at_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_asc.TEXT'),
    value: 'created_at_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.unread.TEXT'),
    value: 'unread',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc.TEXT'),
    value: 'priority_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_asc.TEXT'),
    value: 'priority_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc_created_at_asc.TEXT'),
    value: 'priority_desc_created_at_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_asc.TEXT'),
    value: 'waiting_since_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_desc.TEXT'),
    value: 'waiting_since_desc',
  },
]);

const saveSelectedFilter = (type, value) => {
  updateUISettings({
    conversations_filter_by: {
      status: type === 'status' ? value : currentStatusFilter.value,
      order_by: type === 'sort' ? value : currentSortBy.value,
    },
  });
};

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};
</script>

<template>
  <div class="relative flex">
    <NextButton
      v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      icon="i-lucide-arrow-up-down"
      slate
      faded
      xs
      :aria-expanded="showActionsDropdown"
      :aria-label="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      @click="toggleDropdown()"
    />
    <div
      v-if="showActionsDropdown"
      v-on-click-outside="() => toggleDropdown()"
      class="absolute top-full z-40 mt-1.5 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl dark:border-n-strong"
      :class="{
        'ltr:left-0 rtl:right-0': !isOnExpandedLayout,
        'ltr:right-0 rtl:left-0': isOnExpandedLayout,
      }"
      role="dialog"
      :aria-label="$t('CHAT_LIST.CHAT_SORT.TITLE')"
      @keydown.esc="toggleDropdown(false)"
    >
      <header class="flex items-center gap-2 border-b border-n-weak px-4 py-3">
        <span
          class="grid size-7 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <span class="i-hugeicons-sorting-05 size-4" />
        </span>
        <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ $t('CHAT_LIST.CHAT_SORT.TITLE') }}
        </h3>
      </header>
      <section class="px-4 pb-3 pt-3">
        <h4 class="mb-2 text-xs font-medium text-n-slate-11">
          {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
        </h4>
        <div class="grid grid-cols-2 gap-1.5">
          <button
            v-for="option in chatStatusOptions"
            :key="option.value"
            type="button"
            class="reset-base m-0 flex h-8 min-w-0 items-center justify-center rounded-lg border px-2 text-xs font-medium transition-colors"
            :class="
              currentStatusFilter === option.value
                ? 'border-n-brand/40 bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :title="option.label"
            :aria-pressed="currentStatusFilter === option.value"
            @click="handleStatusChange(option.value)"
          >
            <span class="truncate">{{ option.label }}</span>
          </button>
        </div>
      </section>
      <section class="border-t border-n-weak px-2 pb-2 pt-3">
        <h4 class="mb-1.5 px-2 text-xs font-medium text-n-slate-11">
          {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
        </h4>
        <ul class="mb-0 max-h-60 list-none overflow-y-auto" role="listbox">
          <li v-for="option in chatSortOptions" :key="option.value">
            <button
              type="button"
              role="option"
              :aria-selected="currentSortBy === option.value"
              class="reset-base m-0 flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors"
              :class="
                currentSortBy === option.value
                  ? 'bg-n-alpha-2 font-medium text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="handleSortChange(option.value)"
            >
              <span class="min-w-0 truncate">{{ option.label }}</span>
              <span
                v-if="currentSortBy === option.value"
                class="i-hugeicons-tick-02 size-4 shrink-0 text-n-brand"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
